import { Action } from "@/components/ai-elements/actions"
import {
  PromptInput,
  PromptInputBody,
  PromptInputButton,
  PromptInputFooter,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputTools,
} from "@/components/ai-elements/prompt-input"
import { LogoMark } from "@/components/common/Logo/LogoMark"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import type {
  PendingConfirmation,
  UploadingFile,
} from "@/contexts/AgentChatContext"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useRateLimitContext } from "@/contexts/RateLimitContext"
import { AgentConfirmationBar } from "@/features/agent-chat/components/AgentConfirmationBar"
import { AI_CHAT_MOCK_QUESTION_KEY, useOnboarding } from "@/features/onboarding"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { ConversationFile } from "@/services/api/chatFilesService"
import {
  chatAttachAccept,
  destinationDisplayName,
  useUploadFormats,
} from "@/services/api/uploadFormatsService"
import {
  Copy,
  Download,
  FileText,
  FileType2,
  GlobeIcon,
  Loader2Icon,
  Paperclip,
  PlusIcon,
  X,
} from "lucide-react"
import React, { useEffect, useRef, useState } from "react"
import { ChatMessageQueue } from "./ChatMessageQueue"
import { RateLimitBadge } from "./RateLimitBadge"
import { useChatFileUploadEnabled } from "./hooks/useChatFileUploadEnabled"
import { QueuedChatMessage } from "./hooks/useChatSubmit"

interface ChatInputProps {
  onSubmit: (message: { text?: string; files?: unknown[] }) => void
  isStreaming: boolean
  containerClassName?: string
  /**
   * Tight layout for narrow containers (e.g. the nav-header chat sheet)
   * where Tailwind's `md:` breakpoint is unreliable — it reacts to the
   * browser window's width, not this component's own container width.
   */
  compact?: boolean
  queuedMessages: QueuedChatMessage[]
  onRemoveQueuedMessage: (messageId: string) => void
  onForceSendQueuedMessage: (messageId: string) => void
  onCopy: () => void
  onExport: () => void
  onExportPdf: () => void
  onNewChat: () => void
  hasMessages: boolean
  webSearchEnabled: boolean
  onToggleWebSearch: (enabled: boolean) => void
  /**
   * Pre-fills the (uncontrolled) textarea without submitting, e.g. from the
   * empty-state suggestion chips. `id` changes on every request so the same
   * text can be applied twice.
   */
  prefill?: { text: string; id: number } | null
  /** Gates the attach-file affordance (see useAgentChatEnabled). */
  agentChatEnabled?: boolean
  /**
   * Files attached to the active conversation — already staged server-side,
   * so each one is independently addressable and removable rather than a
   * single slot that a second attach silently replaces.
   */
  stagedFiles?: ConversationFile[]
  /** Files whose staging request hasn't resolved yet — rendered as a loading chip. */
  uploadingFiles?: UploadingFile[]
  onAttachFile?: (file: File) => void | Promise<void>
  onRemoveStagedFile?: (fileId: string) => void | Promise<void>
  /** Answer explicitly targeted via a per-answer "ask agent" button, if any. */
  targetedAnswerLabel?: string | null
  onClearTargetedAnswer?: () => void
  targetedHelpArticleLabel?: string | null
  onClearTargetedHelpArticle?: () => void
  /**
   * A tool call suspended on the user's Confirm / Cancel decision. Rendered
   * fused to the top of the composer, and blocks it: sending another message
   * while one is open interleaves that reply with the resumed tool call's
   * output.
   */
  pendingConfirmation?: PendingConfirmation | null
  onResolveConfirmation?: (confirmed: boolean) => void
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSubmit,
  isStreaming,
  containerClassName,
  compact = false,
  queuedMessages,
  onRemoveQueuedMessage,
  onForceSendQueuedMessage,
  onNewChat,
  onCopy,
  onExport,
  onExportPdf,
  hasMessages,
  webSearchEnabled,
  onToggleWebSearch,
  prefill = null,
  agentChatEnabled = false,
  stagedFiles = [],
  uploadingFiles = [],
  onAttachFile,
  onRemoveStagedFile,
  targetedAnswerLabel = null,
  onClearTargetedAnswer,
  targetedHelpArticleLabel = null,
  onClearTargetedHelpArticle,
  pendingConfirmation = null,
  onResolveConfirmation,
}) => {
  const { translate } = useTranslation()
  const chatRateLimitEnabled = useFeatureFlag("CHAT_RATE_LIMIT")
  const webSearchFeatureEnabled = useFeatureFlag("WEB_SEARCH")
  const pdfDownloadEnabled = useFeatureFlag("CHAT_PDF_DOWNLOAD")
  const chatFileUploadEnabled = useChatFileUploadEnabled()
  // The union across every routable destination, from the backend list — a
  // file is attached before (and often without) a destination being chosen.
  const uploadFormats = useUploadFormats()
  const attachAccept = chatAttachAccept(uploadFormats)
  const { remainingMessages, isUnlimited } = useRateLimitContext()
  const {
    activeTourId,
    currentStepIndex,
    getCurrentTour,
    handleHighlightedAction,
  } = useOnboarding()
  const [inputValue, setInputValue] = useState("")
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const ignoreRejectedHandler = (result: void | Promise<void> | undefined) => {
    if (result && typeof result === "object" && "catch" in result) {
      void result.catch(() => {})
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (file) {
      ignoreRejectedHandler(onAttachFile?.(file))
    }
  }

  // Which chip's remove request is in flight — swaps that chip's X for a
  // spinner until the DELETE actually resolves, instead of the button just
  // sitting there with no feedback for the whole round-trip.
  const [removingFileIds, setRemovingFileIds] = useState<Set<string>>(new Set())

  const handleRemoveFile = async (fileId: string) => {
    setRemovingFileIds((prev) => new Set(prev).add(fileId))
    try {
      await onRemoveStagedFile?.(fileId)
    } catch {
      // AgentChatContext.removeStagedFile already posts the failure reason
      // as a chat message — this local state only owns the spinner.
    } finally {
      setRemovingFileIds((prev) => {
        const next = new Set(prev)
        next.delete(fileId)
        return next
      })
    }
  }

  useEffect(() => {
    if (!hasMessages && !isStreaming) {
      textareaRef.current?.focus()
    }
  }, [hasMessages, isStreaming])

  // The textarea is uncontrolled (PromptInput reads it via FormData on
  // submit), so pre-filling means writing the DOM value directly.
  useEffect(() => {
    if (prefill && textareaRef.current) {
      textareaRef.current.value = prefill.text
      textareaRef.current.focus()
    }
  }, [prefill])

  const isRateLimited =
    chatRateLimitEnabled &&
    !isUnlimited &&
    remainingMessages !== null &&
    remainingMessages <= 0

  // Check if we're on the chat-input-step of AI chat tour
  const currentTour = getCurrentTour()
  const isOnChatInputStep =
    activeTourId === "ai-chat" &&
    currentTour?.steps[currentStepIndex]?.id === "chat-input-step"

  // Pre-fill input when on chat-input-step
  useEffect(() => {
    if (isOnChatInputStep) {
      setInputValue(translate(AI_CHAT_MOCK_QUESTION_KEY as TranslationKeys))
    }
  }, [isOnChatInputStep, translate])

  // A suspended tool call is modal: the composer is locked until it resolves.
  // "confirmed" stays locked too — the tool call it unblocked is still in
  // flight, and executeToolCall is what finally clears the card.
  const isAwaitingConfirmation = agentChatEnabled && !!pendingConfirmation

  const handleSubmit = (message: { text?: string; files?: unknown[] }) => {
    // Belt and braces: the textarea and submit button are both disabled while
    // a confirmation is open, but the form can still be submitted
    // programmatically (requestSubmit from the tour, a stray Enter mid-render).
    if (isAwaitingConfirmation) {
      return
    }
    // If on tour chat-input-step, advance tour instead of sending real message
    if (isOnChatInputStep) {
      setInputValue("")
      handleHighlightedAction("chat-input-step")
      return
    }
    // Normal submit
    onSubmit(message)
  }

  return (
    <div className="relative z-20 w-full border-t border-border/50 bg-linear-to-t from-grey1/80 via-background/95 to-transparent">
      <div
        className={`${compact ? "px-XS" : "px-S md:px-XXL"} pb-S pt-M ${containerClassName ?? ""}`}
      >
        <ChatMessageQueue
          queuedMessages={queuedMessages}
          onRemoveQueuedMessage={onRemoveQueuedMessage}
          onForceSendQueuedMessage={onForceSendQueuedMessage}
        />

        {agentChatEnabled &&
          (stagedFiles.length > 0 || uploadingFiles.length > 0) && (
            <div className="mx-auto mb-2 flex max-w-5xl flex-wrap gap-2">
              {uploadingFiles.map((file) => (
                <span
                  key={file.id}
                  className="inline-flex max-w-full items-center gap-2 rounded-md border border-border/50 bg-grey1/70 px-3 py-1.5 text-xs text-muted-foreground"
                >
                  <Loader2Icon className="h-3 w-3 shrink-0 animate-spin" />
                  <span className="max-w-[220px] truncate">
                    {file.fileName}
                  </span>
                </span>
              ))}
              {stagedFiles.map((file) => (
                <span
                  key={file.id}
                  className="inline-flex max-w-full items-center gap-2 rounded-md border border-border/50 bg-grey1/70 px-3 py-1.5 text-xs text-muted-foreground"
                >
                  <Paperclip className="h-3 w-3 shrink-0" />
                  <span className="max-w-[220px] truncate">
                    {file.fileName}
                  </span>
                  {file.status === "routed" && file.routedTo && (
                    <span className="shrink-0 text-primary">
                      {translate("AI_CHAT/FILE_ROUTED_TO", {
                        // routedTo is the wire slug ("supplier_declaration") —
                        // never shown raw, translated to its display name first.
                        destination: destinationDisplayName(
                          file.routedTo,
                          translate,
                        ),
                      })}
                    </span>
                  )}
                  {removingFileIds.has(file.id) ? (
                    <Loader2Icon className="h-3 w-3 shrink-0 animate-spin text-muted-foreground" />
                  ) : (
                    <button
                      type="button"
                      aria-label={translate("AI_CHAT/REMOVE_ATTACHED_FILE", {
                        fileName: file.fileName,
                      })}
                      onClick={() => void handleRemoveFile(file.id)}
                      className="shrink-0 text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}

        {agentChatEnabled && targetedAnswerLabel && (
          <div className="mx-auto mb-2 flex max-w-5xl">
            <span className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-1 text-xs text-primary">
              <LogoMark className="h-3 w-3 shrink-0" />
              <span
                className="truncate"
                data-testid="targeted-answer-chip-label"
              >
                {translate("AI_CHAT/TARGETED_ANSWER_PREFIX")}{" "}
                {targetedAnswerLabel}
              </span>
              <button
                type="button"
                aria-label="Clear targeted answer"
                onClick={onClearTargetedAnswer}
                className="shrink-0 text-primary/70 hover:text-primary"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          </div>
        )}

        {/* Its own chip rather than reusing the answer one: a reader can plausibly be
            asking about an article while a questionnaire answer is still targeted, and
            collapsing the two would silently drop one of them. */}
        {agentChatEnabled && targetedHelpArticleLabel && (
          <div className="mx-auto mb-2 flex max-w-5xl">
            <span className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-1 text-xs text-primary">
              <LogoMark className="h-3 w-3 shrink-0" />
              <span
                className="truncate"
                data-testid="targeted-help-article-chip-label"
              >
                {translate("AI_CHAT/TARGETED_ARTICLE_PREFIX")}{" "}
                {targetedHelpArticleLabel}
              </span>
              <button
                type="button"
                aria-label="Clear targeted help article"
                onClick={onClearTargetedHelpArticle}
                className="shrink-0 text-primary/70 hover:text-primary"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          </div>
        )}

        {agentChatEnabled && (
          <AgentConfirmationBar
            confirmation={pendingConfirmation}
            onResolve={(confirmed) => onResolveConfirmation?.(confirmed)}
          />
        )}

        <PromptInput
          onSubmit={handleSubmit}
          autoFocus
          className={`relative z-0 mx-auto max-w-5xl rounded-lg border border-primary/20`}
          id="chat-input-step"
        >
          <PromptInputBody>
            <PromptInputTextarea
              ref={textareaRef}
              autoFocus={!hasMessages && !isStreaming}
              disabled={isAwaitingConfirmation}
              placeholder={translate(
                isAwaitingConfirmation
                  ? "AI_CHAT/CONFIRMATION_BLOCKED_PLACEHOLDER"
                  : "AI_CHAT/PLACEHOLDER",
              )}
              // field-sizing-fixed keeps the fixed height the box has always had:
              // the vendored textarea's field-sizing-content never generated CSS
              // under Tailwind 3, and would make it grow with its content.
              className="min-h-20 px-M pt-M text-[15px] field-sizing-fixed placeholder:text-muted-foreground/80"
              value={isOnChatInputStep ? inputValue : undefined}
              onChange={
                isOnChatInputStep
                  ? (e) => setInputValue(e.target.value)
                  : undefined
              }
            />
          </PromptInputBody>
          <PromptInputFooter className="border-t border-border/50 bg-grey1/50 px-S py-S">
            <PromptInputTools className="flex-wrap gap-2">
              <PromptInputButton
                onClick={onNewChat}
                variant={"ghost"}
                className="border border-border/50 bg-grey1/70 text-foreground hover:border-primary/50 hover:bg-primary/10 hover:text-primary"
              >
                <PlusIcon />
                <span>{translate("AI_CHAT/NEW_CONVERSATION")}</span>
              </PromptInputButton>

              {agentChatEnabled && chatFileUploadEnabled && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={attachAccept}
                    className="hidden"
                    onChange={handleFileChange}
                  />
                  <Action
                    tooltip={translate("AI_CHAT/ATTACH_FILE")}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isAwaitingConfirmation}
                    label={translate("AI_CHAT/ATTACH_FILE")}
                    className="border border-border/50 bg-grey1/70 hover:border-primary/50 hover:bg-primary/10 hover:text-primary"
                  >
                    <Paperclip />
                  </Action>
                </>
              )}

              {pdfDownloadEnabled ? (
                <DropdownMenu>
                  <DropdownMenuTrigger
                    type="button"
                    disabled={!hasMessages}
                    aria-label={translate("AI_CHAT/DOWNLOAD")}
                    title={translate("AI_CHAT/DOWNLOAD")}
                    className="relative inline-flex size-9 items-center justify-center rounded-md border border-border/50 bg-grey1/70 p-1.5 text-muted-foreground transition-colors hover:border-primary/50 hover:bg-primary/10 hover:text-primary focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0"
                  >
                    <Download />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem onClick={onExportPdf}>
                      <FileType2 className="mr-2 h-4 w-4" />
                      {translate("AI_CHAT/DOWNLOAD_PDF")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={onExport}>
                      <FileText className="mr-2 h-4 w-4" />
                      {translate("AI_CHAT/DOWNLOAD_TXT")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Action
                  tooltip={translate("AI_CHAT/DOWNLOAD")}
                  onClick={onExport}
                  disabled={!hasMessages}
                  className="border border-border/50 bg-grey1/70 hover:border-primary/50 hover:bg-primary/10 hover:text-primary"
                >
                  <Download />
                </Action>
              )}
              <Action
                tooltip={translate("AI_CHAT/COPY")}
                onClick={onCopy}
                className="border border-border/50 bg-grey1/70 hover:border-primary/50 hover:bg-primary/10 hover:text-primary"
              >
                <Copy />
              </Action>

              {webSearchFeatureEnabled && (
                <Action
                  tooltip={translate("AI_CHAT/WEB_SEARCH/TOGGLE_LABEL")}
                  onClick={() => onToggleWebSearch(!webSearchEnabled)}
                  disabled={isAwaitingConfirmation}
                  className={
                    webSearchEnabled
                      ? "border border-primary bg-primary text-primary-foreground shadow-[0_0_18px_hsl(var(--primary)/0.22)] hover:bg-yellow2 hover:text-primary-foreground"
                      : "border border-border/50 bg-grey1/70 hover:border-primary/50 hover:bg-primary/10 hover:text-primary"
                  }
                >
                  <GlobeIcon />
                </Action>
              )}

              <RateLimitBadge />
            </PromptInputTools>
            <div id="chat-submit-button">
              <PromptInputSubmit
                status={isStreaming ? "submitted" : "ready"}
                disabled={isRateLimited || isAwaitingConfirmation}
                className="bg-primary text-primary-foreground shadow-[0_0_22px_hsl(var(--primary)/0.2)] hover:bg-yellow2 disabled:bg-muted disabled:text-muted-foreground"
                aria-label={translate("AI_CHAT/SUBMIT")}
                title={
                  isAwaitingConfirmation
                    ? translate("AI_CHAT/CONFIRMATION_BLOCKED_PLACEHOLDER")
                    : isRateLimited
                      ? translate("AI_CHAT/RATE_LIMIT_EXCEEDED")
                      : undefined
                }
              />
            </div>
          </PromptInputFooter>
        </PromptInput>
      </div>
    </div>
  )
}
